import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BrainGraphDTO } from "@app-factory/shared";

/**
 * Silový graf brainu na canvasu.
 *
 * Canvas, ne SVG: u pár set uzlů by SVG překreslovalo celý DOM v každém
 * snímku a graf by se začal trhat. Vlastní simulace místo knihovny: je to
 * odpuzování, pružiny a tlumení, tedy pár desítek řádků — a webová appka
 * zatím nemá žádnou vizualizační závislost, kterou by stálo za to přidávat
 * kvůli jedné obrazovce.
 *
 * Co v grafu čteme (a v seznamu bychom nepoznali):
 *  — izolovaná tečka = poznámka, na kterou nic neodkazuje,
 *  — velký uzel = problém, který se vrací (velikost dle počtu výskytů),
 *  — prázdný kroužek = odkaz na poznámku, která ještě neexistuje,
 *  — přerušovaný obrys = poznámka bez spouštěče, tedy mimo rozcestník.
 *
 * Pohled se dá přiblížit a posunout: u pár desítek poznámek se graf ještě
 * vejde, u dvou set už ne — bez zoomu se překrývající uzly nedaly přečíst
 * ani rozkliknout. Kolečko přibližuje k místu pod kurzorem, tažení posouvá,
 * dvojklik vrátí výchozí pohled; na dotyku funguje sevření dvěma prsty.
 */

type Node = {
  slug: string;
  title: string;
  scope: "FACTORY" | "PROJECT";
  kind: "lesson" | "map";
  occurrences: number;
  indexed: boolean;
  linked: boolean;
  missing: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
};

const REPULSION = 5200;
const SPRING = 0.012;
/**
 * Klidová délka pružiny se počítá z velikosti plátna, ne napevno: na velkém
 * panelu by pevná hodnota nechala graf schoulený uprostřed a většina plochy
 * by zůstala prázdná.
 */
function springLength(w: number, h: number, nodes: number): number {
  const room = Math.sqrt((w * h) / Math.max(4, nodes));
  return Math.min(180, Math.max(70, room * 0.55));
}
/** Slabé přitahování ke středu, aby se odloučené shluky neodplavily z plátna. */
const GRAVITY = 0.0016;
const DAMPING = 0.86;
/**
 * Rozložení se **nedopočítává na obrazovce**. Dřív se simulace kreslila
 * snímek po snímku, takže se graf před očima rozlétal a slézal se zpátky;
 * s přibývajícími odkazy to trvalo dýl a dýl a dívat se na to nešlo.
 * Teď se odsimuluje najednou, s chladnutím, a vykreslí se až usazený.
 */
const TICKS = 320;
/** Strop kroku uzlu: bez něj jedna prudká síla vystřelí uzel mimo plátno. */
const MAX_STEP = 30;

function radius(n: Node): number {
  return n.missing ? 4 : 5 + Math.min(11, Math.sqrt(n.occurrences) * 3.2);
}

export function BrainGraph({
  graph,
  selected,
  onSelect,
}: {
  graph: BrainGraphDTO;
  selected: string | null;
  onSelect: (slug: string | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const nodesRef = useRef<Node[]>([]);
  /** Pohled: měřítko a posun v pixelech od středu plátna. */
  const viewRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const zoomApiRef = useRef<{
    zoomBy: (factor: number) => void;
    reset: () => void;
  } | null>(null);
  const [zoom, setZoom] = useState(1);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  // Simulace se po usazení zastaví, takže výběr sám o sobě nic nepřekreslí.
  // Kreslicí funkci si proto podržíme a při změně výběru ji zavoláme.
  const drawRef = useRef<(() => void) | null>(null);

  /**
   * Otisk dat. Refetch vrací nový objekt se stejným obsahem — bez otisku by
   * se rozložení přepočítalo a obrázek poskočil, i když se nic nezměnilo.
   */
  const signature = useMemo(
    () =>
      [
        graph.nodes.map((n) => `${n.slug}:${n.occurrences}`).join(","),
        graph.edges.map((e) => `${e.from}>${e.to}`).join(","),
        graph.missing.join(","),
      ].join("|"),
    [graph],
  );
  const graphRef = useRef(graph);
  graphRef.current = graph;

  // Rozložení se přepočítává jen při změně dat. Deterministický start (žádný
  // Math.random) — jinak by graf po každém refetchi vypadal jinak a člověk by
  // v něm ztrácel orientaci.
  useEffect(() => {
    const all = [
      ...graphRef.current.nodes.map((n) => ({ ...n, missing: false })),
      ...graphRef.current.missing.map((slug) => ({
        slug,
        title: slug,
        scope: "FACTORY" as const,
        kind: "lesson" as const,
        occurrences: 0,
        indexed: false,
        linked: true,
        missing: true,
      })),
    ];
    // Startovní rozložení je ELIPSA podle poměru stran plátna, ne kruh: plátno
    // je široké a nízké, takže z kruhu by se rovnováha ustálila do svislého
    // provazu a šířka by zůstala nevyužitá.
    const rect = canvasRef.current?.getBoundingClientRect();
    const aspect =
      rect && rect.height > 0 ? Math.min(2.2, rect.width / rect.height) : 1.6;
    nodesRef.current = all.map((n, i) => {
      const angle = (i / Math.max(1, all.length)) * Math.PI * 2;
      const r = 70 + (i % 7) * 26;
      return {
        ...n,
        x: Math.cos(angle) * r * aspect,
        y: Math.sin(angle) * r,
        vx: 0,
        vy: 0,
      };
    });
  }, [signature]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let stop = false;

    const style = getComputedStyle(document.documentElement);
    const color = (name: string, fallback: string) =>
      style.getPropertyValue(name).trim() || fallback;
    const cyan = color("--accent", "#4de8ff");
    const green = color("--positive", "#47f0a5");
    const amber = color("--warning", "#ffb454");
    const faint = color("--text-tertiary", "#7089b6");
    const ink = color("--text-primary", "#eaf4ff");
    const violet = color("--info", "#b48cff");

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Změna velikosti plátno vymaže, takže se musí hned překreslit. Bez toho
      // zůstane prázdné vždycky, když se rozměr ustálí až po doběhnutí
      // simulace — což u malého grafu (pár uzlů) nastane skoro pokaždé.
      drawRef.current?.();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    /** Jeden krok simulace. `alpha` je chladnutí: 1 na začátku, 0 na konci. */
    const tick = (alpha: number) => {
      const nodes = nodesRef.current;
      const byslug = new Map(nodes.map((n) => [n.slug, n]));

      // odpuzování všech proti všem — při desítkách uzlů je O(n²) levnější
      // než jakákoli prostorová struktura
      for (let i = 0; i < nodes.length; i += 1) {
        const a = nodes[i]!;
        for (let j = i + 1; j < nodes.length; j += 1) {
          const b = nodes[j]!;
          let dx = a.x - b.x;
          let dy = a.y - b.y;
          let d2 = dx * dx + dy * dy;
          if (d2 < 1) {
            // dva uzly přesně na sobě by daly nekonečnou sílu — rozstrčíme je
            dx = (i % 3) - 1 || 1;
            dy = (j % 3) - 1 || 1;
            d2 = 1;
          }
          const f = REPULSION / d2;
          const d = Math.sqrt(d2);
          a.vx += (dx / d) * f;
          a.vy += (dy / d) * f;
          b.vx -= (dx / d) * f;
          b.vy -= (dy / d) * f;
        }
      }

      const rect0 = canvas.getBoundingClientRect();
      const rest = springLength(rect0.width, rect0.height, nodes.length);
      for (const e of graphRef.current.edges) {
        const a = byslug.get(e.from);
        const b = byslug.get(e.to);
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.hypot(dx, dy) || 1;
        const f = (d - rest) * SPRING;
        a.vx += (dx / d) * f;
        a.vy += (dy / d) * f;
        b.vx -= (dx / d) * f;
        b.vy -= (dy / d) * f;
      }

      for (const n of nodes) {
        // vodorovně slabší přitahování — plátno je široké a graf ho má využít
        n.vx -= n.x * GRAVITY * 0.45;
        n.vy -= n.y * GRAVITY;
        n.vx *= DAMPING;
        n.vy *= DAMPING;
        // krok se s chladnutím zkracuje, takže se rozložení nakonec zastaví
        // samo — hustý graf jinak osciluje a nedojede pod žádný práh
        const stepX = Math.max(-MAX_STEP, Math.min(MAX_STEP, n.vx)) * alpha;
        const stepY = Math.max(-MAX_STEP, Math.min(MAX_STEP, n.vy)) * alpha;
        n.x += stepX;
        n.y += stepY;
      }
    };

    /** Odsimuluje rozložení naráz a vykreslí až výsledek. */
    const settle = () => {
      if (stop) return;
      for (let i = 0; i < TICKS; i += 1) {
        // lineární chladnutí: první kroky rozhodnou o tvaru, poslední doladí
        tick(1 - i / TICKS);
      }
      draw();
    };

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const view = viewRef.current;
      // střed plátna posunutý pohledem; souřadnice uzlů se pak jen škálují
      const cx = rect.width / 2 + view.tx;
      const cy = rect.height / 2 + view.ty;
      const k = view.scale;
      ctx.clearRect(0, 0, rect.width, rect.height);

      const nodes = nodesRef.current;
      const byslug = new Map(nodes.map((n) => [n.slug, n]));
      const sel = selectedRef.current;
      const labels: {
        n: Node;
        px: number;
        py: number;
        r: number;
        isSel: boolean;
      }[] = [];

      for (const e of graphRef.current.edges) {
        const a = byslug.get(e.from);
        const b = byslug.get(e.to);
        if (!a || !b) continue;
        const touches = sel != null && (e.from === sel || e.to === sel);
        ctx.beginPath();
        ctx.moveTo(cx + a.x * k, cy + a.y * k);
        ctx.lineTo(cx + b.x * k, cy + b.y * k);
        ctx.strokeStyle = touches ? cyan : faint;
        ctx.globalAlpha = touches ? 0.9 : e.unresolved ? 0.25 : 0.4;
        ctx.setLineDash(e.unresolved ? [3, 3] : []);
        ctx.lineWidth = (touches ? 1.6 : 1) * Math.min(2, k);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      for (const n of nodes) {
        const r = radius(n) * k;
        const px = cx + n.x * k;
        const py = cy + n.y * k;
        // uzel mimo plátno se nekreslí vůbec: jeho popisek se dřív zarovnal
        // dovnitř a hromadil se u kraje jako stěna textu (při přiblížení byla
        // mapa kvůli tomu nečitelná)
        if (px < -r || px > rect.width + r || py < -r || py > rect.height + r)
          continue;
        const isSel = n.slug === sel;
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);

        if (n.missing) {
          // odkaz nikam: prázdný kroužek — poznámka, která má vzniknout
          ctx.strokeStyle = amber;
          ctx.setLineDash([2, 2]);
          ctx.lineWidth = 1.2;
          ctx.stroke();
          ctx.setLineDash([]);
        } else {
          // mapa kódu vs. poučení: v grafu je potřeba je od sebe rozeznat
          ctx.fillStyle =
            n.kind === "map" ? violet : n.scope === "FACTORY" ? green : cyan;
          ctx.globalAlpha = n.linked ? 0.85 : 0.45;
          ctx.fill();
          ctx.globalAlpha = 1;
          if (!n.indexed) {
            // bez spouštěče = nedodělek; přerušovaný obrys to přizná
            ctx.strokeStyle = amber;
            ctx.setLineDash([2, 2]);
            ctx.lineWidth = 1.2;
            ctx.stroke();
            ctx.setLineDash([]);
          }
          if (isSel) {
            ctx.strokeStyle = ink;
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        }

        // popisky se sbírají a kreslí až po uzlech: při dvou stech poznámkách
        // se všechny nevejdou, takže se řeší přednost a překryv (viz níž)
        labels.push({ n, px, py, r, isSel });
      }

      // Popisky odshora dolů podle důležitosti — vybraný, pak ty, které se
      // vracejí nejčastěji. Co by se překrylo s už napsaným, se vynechá:
      // slitá změť jmen je horší než pár jmen a zbytek teček. Přiblížením se
      // uvolní místo a jména se doplní sama.
      ctx.font = `${Math.max(9, Math.min(15, 10 * k))}px ui-monospace, monospace`;
      ctx.textAlign = "center";
      const placed: { x0: number; y0: number; x1: number; y1: number }[] = [];
      labels.sort(
        (a, b) =>
          Number(b.isSel) - Number(a.isSel) || b.n.occurrences - a.n.occurrences,
      );
      for (const { n, px, py, r, isSel } of labels) {
        const label = n.title.slice(0, 28);
        // u uzlu při spodní hraně píšeme popisek NAD něj, jinak by přetekl
        // z plátna nebo se srazil s tím, co je pod grafem
        const below = py + r + 11;
        const y = below > rect.height - 6 ? py - r - 6 : below;
        // a vodorovně ho posuneme dovnitř, ať se neuřízne na krajích —
        // nanejvýš o svou půlku, aby popisek zůstal u svého uzlu
        const half = ctx.measureText(label).width / 2;
        const x = Math.min(
          Math.max(px, Math.min(half + 4, px + half)),
          Math.max(rect.width - half - 4, px - half),
        );
        // rezerva okolo textu: těsné rámečky sice nekolidují, ale jména se
        // pak skoro dotýkají a čtou se pořád špatně
        const box = { x0: x - half - 7, y0: y - 11, x1: x + half + 7, y1: y + 5 };
        const collides = placed.some(
          (q) => box.x0 < q.x1 && box.x1 > q.x0 && box.y0 < q.y1 && box.y1 > q.y0,
        );
        // vybraný uzel má jméno vždycky, i kdyby překryl sousedy
        if (collides && !isSel) continue;
        placed.push(box);
        ctx.fillStyle = isSel ? ink : faint;
        ctx.fillText(label, x, y);
      }
    };

    drawRef.current = draw;
    settle();

    /** Bod na plátně → souřadnice grafu (inverze pohledu). */
    const toGraph = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      const view = viewRef.current;
      return {
        x: (clientX - rect.left - rect.width / 2 - view.tx) / view.scale,
        y: (clientY - rect.top - rect.height / 2 - view.ty) / view.scale,
      };
    };

    const applyZoom = (factor: number, clientX: number, clientY: number) => {
      const view = viewRef.current;
      const next = Math.min(4, Math.max(0.3, view.scale * factor));
      if (next === view.scale) return;
      const rect = canvas.getBoundingClientRect();
      // bod pod kurzorem musí zůstat na místě, jinak zoom „utíká"
      const px = clientX - rect.left - rect.width / 2;
      const py = clientY - rect.top - rect.height / 2;
      const ratio = next / view.scale;
      view.tx = px - (px - view.tx) * ratio;
      view.ty = py - (py - view.ty) * ratio;
      view.scale = next;
      setZoom(next);
      draw();
    };

    const onWheel = (ev: WheelEvent) => {
      // bez preventDefault by kolečko rolovalo stránkou pod grafem
      ev.preventDefault();
      applyZoom(Math.exp(-ev.deltaY * 0.0015), ev.clientX, ev.clientY);
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });

    // tažení = posun; drobný pohyb se pořád počítá jako kliknutí na uzel
    const pointers = new Map<number, { x: number; y: number }>();
    let dragging = false;
    let moved = 0;
    let pinchDist = 0;

    const onPointerDown = (ev: PointerEvent) => {
      canvas.setPointerCapture(ev.pointerId);
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (pointers.size === 1) {
        dragging = true;
        moved = 0;
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinchDist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      }
    };

    const onPointerMove = (ev: PointerEvent) => {
      const prev = pointers.get(ev.pointerId);
      if (!prev) return;
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
        if (pinchDist > 0 && dist > 0) {
          applyZoom(dist / pinchDist, (a!.x + b!.x) / 2, (a!.y + b!.y) / 2);
        }
        pinchDist = dist;
        moved = 999;
        return;
      }

      if (!dragging) return;
      const dx = ev.clientX - prev.x;
      const dy = ev.clientY - prev.y;
      moved += Math.abs(dx) + Math.abs(dy);
      viewRef.current.tx += dx;
      viewRef.current.ty += dy;
      draw();
    };

    const endPointer = (ev: PointerEvent) => {
      pointers.delete(ev.pointerId);
      if (pointers.size < 2) pinchDist = 0;
      if (pointers.size === 0) dragging = false;
    };

    const onClick = (ev: MouseEvent) => {
      // po tažení se nevybírá — jinak by posun pohledu vždycky přepnul detail
      if (moved > 4) return;
      const { x, y } = toGraph(ev.clientX, ev.clientY);
      let hit: Node | null = null;
      for (const n of nodesRef.current) {
        if (n.missing) continue;
        if (
          Math.hypot(n.x - x, n.y - y) <=
          radius(n) + 4 / viewRef.current.scale
        )
          hit = n;
      }
      onSelect(hit ? hit.slug : null);
      draw();
    };

    const onDoubleClick = () => {
      viewRef.current = { scale: 1, tx: 0, ty: 0 };
      setZoom(1);
      draw();
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", endPointer);
    canvas.addEventListener("pointercancel", endPointer);
    canvas.addEventListener("click", onClick);
    canvas.addEventListener("dblclick", onDoubleClick);
    zoomApiRef.current = {
      zoomBy: (factor) => {
        const rect = canvas.getBoundingClientRect();
        applyZoom(
          factor,
          rect.left + rect.width / 2,
          rect.top + rect.height / 2,
        );
      },
      reset: onDoubleClick,
    };

    return () => {
      stop = true;
      drawRef.current = null;
      zoomApiRef.current = null;
      ro.disconnect();
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", endPointer);
      canvas.removeEventListener("pointercancel", endPointer);
      canvas.removeEventListener("click", onClick);
      canvas.removeEventListener("dblclick", onDoubleClick);
    };
  }, [signature, onSelect]);

  // překreslit při změně výběru, i když je simulace už usazená
  useEffect(() => {
    drawRef.current?.();
  }, [selected]);

  const zoomBy = useCallback(
    (factor: number) => zoomApiRef.current?.zoomBy(factor),
    [],
  );
  const resetView = useCallback(() => zoomApiRef.current?.reset(), []);

  return (
    <div className="relative h-full w-full">
      {/*
        Plátno je `absolute inset-0`, ne `h-full`: s výškou v toku se rozměr
        točil dokola — `resize` nastaví atribut `height` v pixelech zařízení,
        z toho se stane vlastní výška prvku, ta zvětší rodiče, ResizeObserver
        to zachytí a nastaví ještě větší. Na retina displeji se výška po
        každém kole zdvojnásobila a graf ujížděl dolů.
      */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full cursor-grab touch-none active:cursor-grabbing"
        style={{ display: "block" }}
        data-testid="brain-graph"
      />
      <div className="absolute right-2 top-2 flex items-center gap-1">
        <ZoomButton
          label="−"
          title="Oddálit"
          onClick={() => zoomBy(1 / 1.25)}
          testId="brain-zoom-out"
        />
        <ZoomButton
          label="+"
          title="Přiblížit"
          onClick={() => zoomBy(1.25)}
          testId="brain-zoom-in"
        />
        <button
          type="button"
          onClick={resetView}
          title="Výchozí pohled (nebo dvojklik)"
          data-testid="brain-zoom-reset"
          className="af-mono cursor-pointer px-2 py-1 text-[10px]"
          style={{
            borderRadius: "var(--radius-chip)",
            border: "1px solid var(--border-default)",
            background: "var(--surface-inset)",
            color: "var(--text-tertiary)",
          }}
        >
          {Math.round(zoom * 100)} %
        </button>
      </div>
    </div>
  );
}

function ZoomButton({
  label,
  title,
  onClick,
  testId,
}: {
  label: string;
  title: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      data-testid={testId}
      className="flex h-7 w-7 cursor-pointer items-center justify-center text-[14px] leading-none"
      style={{
        borderRadius: "var(--radius-chip)",
        border: "1px solid var(--border-default)",
        background: "var(--surface-inset)",
        color: "var(--text-secondary)",
      }}
    >
      {label}
    </button>
  );
}
